import React from 'react';
import { motion } from 'framer-motion';
import { useMeteoCache } from '../hooks/useMeteoCache';
import type { WeatherData } from '../hooks/useMeteoCache';
import { useLanguage } from '../i18n/LanguageContext';
const EMPTY_DATA: WeatherData = {
  temp: '—',
  dew: '—',
  humidity: '—',
  windSpeed: '—',
  windGust: '—',
  windDir: '—',
  civilDawn: '—',
  civilDusk: '—'
};
export function Meteo() {
  const {
    t
  } = useLanguage();
  const {
    weather,
    qnh,
    loading,
    weatherError
  } = useMeteoCache();
  const data = weather || EMPTY_DATA;
  const qnhDisplay = qnh || '—';
  const error = weatherError;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.8,
    delay: 0.5
  }} className="w-full max-w-7xl mx-auto px-6 mb-12 relative z-20" data-id="element-144" data-mp="1t850h23n8lxg">
      {/* Mobile: METEO heading + QNH centered above data */}
      <div className="lg:hidden text-center mb-6" data-id="element-145" data-mp="2znp2nr8yi428">
        <h3 className="text-[#38bdf8] font-thin text-2xl tracking-[0.2em] uppercase mb-1" data-id="element-146" data-mp="3r5owc2t4wt79">
          {t.meteo.title}
        </h3>
        <p className="text-sm" data-id="element-147" data-mp="3w2f9ah9n78og">
          <span className="text-[#38bdf8] font-bold" data-id="element-148" data-mp="fe4xbdtj87">QNH</span>
          <span className="text-[#38bdf8]/60 font-thin mx-1" data-id="element-149" data-mp="4y8hzjp7q15b">LKPR</span>
          <span className={`text-[#38bdf8] font-bold font-mono ${qnhDisplay === '—' ? 'animate-pulse opacity-30' : ''}`} data-id="element-150" data-mp="9x1y7jvvw696">
            {qnhDisplay}
          </span>
        </p>
      </div>

      <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8 lg:gap-12" data-id="element-151" data-mp="3dek1rf4mir3s">
        {/* Desktop: Label with QNH */}
        <div className="hidden lg:block shrink-0" data-id="element-152" data-mp="3iaim8rmtzoyx">
          <div className="border-l-2 border-[#38bdf8] pl-4 flex flex-col gap-1" data-id="element-153" data-mp="3n3k2p42orwkm">
            <h3 className="text-[#38bdf8] font-thin text-2xl tracking-[0.2em] uppercase" data-id="element-154" data-mp="2po667emtya4g">
              {t.meteo.title}
            </h3>
            <p className="text-sm" data-id="element-155" data-mp="2ulpxbf4mmhdt">
              <span className="text-[#38bdf8] font-bold" data-id="element-156" data-mp="2uko9sb6fc2f7">QNH</span>
              <span className="text-[#38bdf8]/60 font-thin mx-1" data-id="element-157" data-mp="2mkqnxk737mda">LKPR</span>
              <span className={`text-[#38bdf8] font-bold font-mono ${qnhDisplay === '—' ? 'animate-pulse opacity-30' : ''}`} data-id="element-158" data-mp="2ho06asfmwipr">
                {qnhDisplay}
              </span>
            </p>
          </div>
        </div>

        {/* Data Grid — centered on mobile */}
        <div className="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-8 lg:gap-4" data-id="element-159" data-mp="23mnvswj8t9b9">
          <WeatherItem label={t.meteo.temp} value={data.temp} unit="°C" loading={loading} data-id="element-160" data-mp="1ypfipacybvgv" />
          <WeatherItem label={t.meteo.dew} value={data.dew} unit="°C" loading={loading} data-id="element-161" data-mp="28mjxuq8dqqo6" />
          <WeatherItem label={t.meteo.humidity} value={data.humidity} unit="%" loading={loading} data-id="element-162" data-mp="3ozqtd6vqo2wg" />
          <WeatherItem label={t.meteo.wind} value={data.windSpeed} unit="kt" loading={loading} data-id="element-163" data-mp="3rl84lod0bj8v" />
          <WeatherItem label={t.meteo.gust} value={data.windGust} unit="kt" loading={loading} data-id="element-164" data-mp="gi4bzkwmxnu" />
          <WeatherItem label={t.meteo.dir} value={data.windDir} unit="°" loading={loading} data-id="element-165" data-mp="3mlvkvchfmsai" />

          {/* Dawn/Dusk Group */}
          <div className="col-span-2 flex flex-col lg:items-start gap-1.5 border-t sm:border-t-0 sm:border-l border-white/10 pt-4 sm:pt-0 sm:pl-4 mt-2 sm:mt-0 ml-[50px] items-end justify-center" data-id="element-166" data-mp="j5ex1z0nli3y">
            <div className="flex items-baseline gap-2 text-xs text-white/40" data-id="element-167" data-mp="9flo75rwbh3e">
              <span className="uppercase tracking-wider whitespace-nowrap" data-id="element-168" data-mp="9dio1sx147ky">
                {t.meteo.dawn}
              </span>
              <span className={`font-mono text-sm whitespace-nowrap ${loading ? 'text-[#38bdf8]/30 animate-pulse' : 'text-[#38bdf8]'}`} data-id="element-169" data-mp="11vnzna0crbw6">
                {data.civilDawn}
              </span>
            </div>
            <div className="flex gap-2 text-xs text-white/40 items-center justify-start" data-id="element-170" data-mp="35d4co159oi0x">
              <span className="uppercase tracking-wider whitespace-nowrap" data-id="element-171" data-mp="30fucbqgcalyb">
                {t.meteo.dusk}
              </span>
              <span className={`font-mono text-sm whitespace-nowrap ${loading ? 'text-[#38bdf8]/30 animate-pulse' : 'text-[#38bdf8]'}`} data-id="element-172" data-mp="2wf0qd2mgjh82">
                {data.civilDusk}
              </span>
            </div>
          </div>
        </div>
      </div>

      {error && <p className="text-white/20 text-[10px] text-center mt-4 font-mono" data-id="element-173" data-mp="3jbua5hw00jpt">
          {t.meteo.unavailable} ({error})
        </p>}
    </motion.div>;
}
function WeatherItem({
  label,
  value,
  unit,
  loading
}: {
  label: string;
  value: string;
  unit: string;
  loading?: boolean;
}) {
  return <div className="flex flex-col items-center lg:items-start" data-id="element-174" data-mp="avqzr4di4gxs">
      <span className="text-[10px] uppercase tracking-widest text-white/40 mb-1" data-id="element-175" data-mp="1tln2cyyhdjp8">
        {label}
      </span>
      <div className="flex items-baseline" data-id="element-176" data-mp="1yk86vakrmpjh">
        <span className={`text-3xl font-extralight tracking-tighter ${loading ? 'text-[#38bdf8]/30 animate-pulse' : 'text-[#38bdf8]'}`} data-id="element-177" data-mp="22l9wr0sslp8s">
          {value}
        </span>
        <span className="ml-1 text-sm text-[#38bdf8]/50 font-light" data-id="element-178" data-mp="27i0d7p8iemuq">
          {unit}
        </span>
      </div>
    </div>;
}